import { Component, DestroyRef, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  SalesInsightsActivityType,
  SalesInsightsActivityTypeKind,
  SalesInsightsActivityTypeRequest
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import { SalesInsightsService } from '../../services/sales-insights.service';
import { salesInsightsReadableName } from '../../utils/sales-insights-activities';
import { SALES_INSIGHTS_TYPE_ICONS, salesInsightsActivityTypeGroups } from '../../utils/sales-insights-activity-types';
import { salesInsightsColor, salesInsightsSeries } from '../../utils/sales-insights-theme';

export interface SalesInsightsActivityTypeView {
  type: SalesInsightsActivityType;
  name: string;
  rgb: string;
  icon: string;
}

export interface SalesInsightsActivityTypeGroupView {
  kind: SalesInsightsActivityTypeKind;
  labelKey: string;
  rows: SalesInsightsActivityTypeView[];
}

@Component({
  selector: 'lib-sales-insights-activity-types',
  standalone: false,
  templateUrl: './sales-insights-activity-types.component.html',
  styleUrl: './sales-insights-activity-types.component.scss'
})
export class SalesInsightsActivityTypesComponent implements OnInit {
  // Emits after every saved change, true when a type was added to the metadata.
  @Output() changed = new EventEmitter<boolean>();

  activeModal = inject(NgbActiveModal);
  protected salesInsightsService = inject(SalesInsightsService);
  protected activityService = inject(SalesInsightsActivityService);
  private destroyRef = inject(DestroyRef);

  readonly icons = SALES_INSIGHTS_TYPE_ICONS.map(icon => `fa-solid ${icon}`);
  readonly palette = salesInsightsSeries().map((rgb, index) => ({ color: index + 1, rgb }));

  groups: SalesInsightsActivityTypeGroupView[] = [];
  loading = true;
  error: string | null = null;
  saving = new Set<string>();
  creating = false;
  newName = '';

  ngOnInit(): void {
    this.load();
  }

  update(view: SalesInsightsActivityTypeView, change: SalesInsightsActivityTypeRequest): void {
    const type = view.type;
    if (this.saving.has(type.code)) {
      return;
    }
    const request: SalesInsightsActivityTypeRequest = {
      countsAsVisit: type.countsAsVisit,
      color: type.color ?? null,
      icon: type.icon ?? null,
      active: type.active,
      ...change
    };
    this.error = null;
    this.saving.add(type.code);
    this.salesInsightsService
      .updateActivityType(type.code, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.delete(type.code);
          this.changed.emit(false);
          this.load();
        },
        error: err => {
          this.saving.delete(type.code);
          this.error = this.message(err);
          this.load();
        }
      });
  }

  create(): void {
    const name = this.newName.trim();
    if (!name || this.creating) {
      return;
    }
    this.error = null;
    this.creating = true;
    this.salesInsightsService
      .createActivityType({ name, countsAsVisit: false })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.creating = false;
          this.newName = '';
          this.changed.emit(true);
          this.load();
        },
        error: err => {
          this.creating = false;
          this.error = this.message(err);
        }
      });
  }

  private load(): void {
    this.salesInsightsService
      .getActivityTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: types => {
          this.groups = salesInsightsActivityTypeGroups(types || []).map(group => ({
            kind: group.kind,
            labelKey: group.labelKey,
            rows: group.types.map(type => this.toView(type, types || []))
          }));
          this.loading = false;
        },
        error: err => {
          this.error = this.message(err);
          this.loading = false;
        }
      });
  }

  private toView(type: SalesInsightsActivityType, types: SalesInsightsActivityType[]): SalesInsightsActivityTypeView {
    return {
      type,
      name: salesInsightsReadableName(type.name || type.code),
      rgb: salesInsightsColor(this.activityService.typeColorIndex(type.code, null, types)),
      icon: this.activityService.typeIcon(type.code, null, types)
    };
  }

  private message(err: any): string {
    return err?.error?.error || err?.error?.message || 'SALES_INSIGHTS_SAVE_FAILED';
  }
}
